import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Avatar,
  CardActionArea,
  useTheme
} from '@mui/material'
import {
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  WaterDrop as WaterDropIcon
} from '@mui/icons-material'
import StatusBadge from '../Common/StatusBadge'

const PoolCard = ({ pool, onSelect, onEdit, isSelected = false }) => {
  const theme = useTheme()
  
  const getPoolTypeColor = (type) => {
    switch (type) {
      case 'vinyl_liner': return 'primary'
      case 'plaster': return 'secondary' 
      case 'fiberglass': return 'success'
      default: return 'default'
    }
  }

  const getPoolTypeIcon = (type) => {
    switch (type) {
      case 'vinyl_liner': return '🏊‍♀️'
      case 'plaster': return '🏛️'
      case 'fiberglass': return '✨'
      default: return '🏊'
    }
  }

  return (
    <Card
      sx={{
        position: 'relative',
        height: '100%',
        transition: 'all 0.3s ease-in-out',
        border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
        boxShadow: isSelected ? `0 8px 25px ${theme.palette.primary.main}33` : 2,
        background: isSelected 
          ? `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.main}08 100%)`
          : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 30px ${theme.palette.grey[400]}40`,
        }
      }}
    >
      <CardActionArea 
        onClick={() => onSelect(pool)}
        sx={{ height: '100%', p: 0 }}
      >
        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header with Icon and Edit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar
                sx={{
                  background: isSelected 
                    ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                    : `linear-gradient(45deg, ${theme.palette.grey[400]}, ${theme.palette.grey[300]})`,
                  fontSize: '1.2rem'
                }}
              >
                {getPoolTypeIcon(pool.pool_type)}
              </Avatar>
              {isSelected && (
                <CheckCircleIcon 
                  color="primary" 
                  sx={{ fontSize: 24 }}
                />
              )}
            </Box>
            <IconButton
              onClick={(e) => {
                e.stopPropagation()
                onEdit(pool)
              }}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'primary.light'
                }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Pool Name */}
          <Typography 
            variant="h6" 
            component="h3" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: isSelected ? 'primary.main' : 'text.primary',
              mb: 1
            }}
          >
            {pool.name}
          </Typography>

          {/* Pool Details */}
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Volume */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WaterDropIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                <Typography component="span" sx={{ fontWeight: 500 }}>
                  {pool.volume_gallons?.toLocaleString()}
                </Typography>
                {' gallons'}
              </Typography>
            </Box>

            {/* Pool Type Chip */}
            <Box>
              <StatusBadge
                label={pool.pool_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                color={getPoolTypeColor(pool.pool_type)}
                size="small"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  opacity: isSelected ? 1 : 0.8
                }}
              />
            </Box>
          </Box>

          {/* Selection Indicator */}
          {isSelected && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 0,
                height: 0,
                borderLeft: '20px solid transparent',
                borderTop: `20px solid ${theme.palette.primary.main}`,
              }}
            />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

export default PoolCard